import { mapSupplierToFormValues } from 'src/sections/supplier/utils/enrichSupplierForEdit';
import { toSupplierFieldValue, toSupplierPhoneValue } from 'src/sections/supplier/utils/supplierFormValues';
import { CreateOfflineSupplierFormValues } from 'src/types/manage-suppliers/CreateOfflineSupplierForm';
import { ManageSupplier } from 'src/types/manage-suppliers/ManageSupplier';
import { OfflineSupplierUpsertRequest } from 'src/types/manage-suppliers/OfflineSupplierUpsert';
import {
  ManageSupplierEditContext,
  SupplierFieldAccess,
  SupplierFormFieldKey,
  SupplierFormValues,
  SupplierTransmissionType,
} from 'src/types/manage-suppliers/SupplierForm';
import {
  resolveSupplierTransmissionType,
  toApiPoTransmitMethod,
  toApiSupplierStatus,
  toUpsertValidationStatus,
  UPSERT_VALIDATION_PENDING,
} from './supplierFilterItemMappers';

const toUpsertNullableString = (value: string | null | undefined): string | null => {
  const trimmed = toSupplierFieldValue(value);
  return trimmed ? trimmed : null;
};

const copyEditableFormField = <K extends SupplierFormFieldKey>(
  field: K,
  target: SupplierFormValues,
  source: SupplierFormValues
) => {
  target[field] = source[field];
};

interface ResolveOfflineUpsertValuesParams {
  values: SupplierFormValues;
  supplier?: ManageSupplierEditContext;
  fieldAccess?: SupplierFieldAccess;
}

export const resolveOfflineUpsertValues = ({
  values,
  supplier,
  fieldAccess,
}: ResolveOfflineUpsertValuesParams): SupplierFormValues => {
  if (!supplier || !fieldAccess) {
    return values;
  }

  const baseline = mapSupplierToFormValues(supplier);
  const merged: SupplierFormValues = { ...baseline };

  // Include currently editable fields, plus any dirty values from fields that were
  // temporarily unlocked in this session (e.g. Inactive → Active → edit → Inactive).
  // Legacy keeps those edits on save even when the final status locks the form.
  (Object.keys(fieldAccess) as SupplierFormFieldKey[]).forEach((field) => {
    if (fieldAccess[field] || values[field] !== baseline[field]) {
      copyEditableFormField(field, merged, values);
    }
  });

  return merged;
};

interface ResolveValidationFieldsParams {
  transmissionType: SupplierTransmissionType;
  orderEmail: string;
  previousValidationStatus: number;
  previousIsValidation: boolean;
  previousOrderEmail: string;
  previousTransmissionType?: SupplierTransmissionType;
  isCreate: boolean;
}

export const resolveValidationFields = ({
  transmissionType,
  orderEmail,
  previousValidationStatus,
  previousIsValidation,
  previousOrderEmail,
  previousTransmissionType,
  isCreate,
}: ResolveValidationFieldsParams): Pick<OfflineSupplierUpsertRequest, 'validationStatus' | 'isValidation'> => {
  if (transmissionType !== 'email') {
    return { validationStatus: null, isValidation: false };
  }

  if (isCreate) {
    return { validationStatus: UPSERT_VALIDATION_PENDING, isValidation: true };
  }

  const emailChanged = toSupplierFieldValue(orderEmail).trim() !== toSupplierFieldValue(previousOrderEmail).trim();

  const transmissionChangedToEmail =
    previousTransmissionType != null &&
    previousTransmissionType !== 'email' &&
    transmissionType === 'email';

  if (emailChanged || transmissionChangedToEmail) {
    return { validationStatus: UPSERT_VALIDATION_PENDING, isValidation: true };
  }

  return {
    validationStatus: toUpsertValidationStatus(
      previousValidationStatus,
      transmissionType,
      previousIsValidation
    ),
    isValidation: false,
  };
};

const restoreEmailTransmissionIfNeeded = (
  supplier: ManageSupplierEditContext,
  resolvedValues: SupplierFormValues,
  fieldAccess: SupplierFieldAccess
): SupplierFormValues => {
  if (resolvedValues.transmissionType === 'email') {
    return resolvedValues;
  }

  if (fieldAccess.transmissionType) {
    return resolvedValues;
  }

  if (resolveSupplierTransmissionType(supplier) !== 'email') {
    return resolvedValues;
  }

  return {
    ...resolvedValues,
    transmissionType: 'email',
    orderEmail:
      toSupplierFieldValue(resolvedValues.orderEmail) || toSupplierFieldValue(supplier.email),
  };
};

const mapValuesToRequest = (
  values: SupplierFormValues,
  ids: Pick<OfflineSupplierUpsertRequest, 'offlineSupplierId' | 'supplierClientId'>,
  validation: Pick<OfflineSupplierUpsertRequest, 'validationStatus' | 'isValidation'>
): OfflineSupplierUpsertRequest => ({
  offlineSupplierId: ids.offlineSupplierId,
  supplierClientId: ids.supplierClientId,
  supplierCompanyName: toSupplierFieldValue(values.supplierName),
  externalSupplierName: toUpsertNullableString(values.externalSupplierName),
  apId: toUpsertNullableString(values.apId),
  accountNumber: toUpsertNullableString(values.accountNumber),
  addressLine1: toUpsertNullableString(values.address1),
  addressLine2: toUpsertNullableString(values.address2),
  city: toUpsertNullableString(values.city),
  state: toUpsertNullableString(values.stateProvince),
  zipCode: toUpsertNullableString(values.zipPostalCode),
  country: toUpsertNullableString(values.country),
  contact: toUpsertNullableString(values.contactName),
  phoneNumber: toUpsertNullableString(toSupplierPhoneValue(values.phone)),
  faxNumber: null,
  email: toUpsertNullableString(values.orderEmail),
  status: toApiSupplierStatus(values.status),
  poTransmitMethod: toApiPoTransmitMethod(values.transmissionType),
  validationStatus: validation.validationStatus,
  isValidation: validation.isValidation,
});

export const mapCreateFormToOfflineUpsertRequest = (
  values: CreateOfflineSupplierFormValues
): OfflineSupplierUpsertRequest => {
  const formValues: SupplierFormValues = {
    ...values,
    status: 'active',
  };

  const validation = resolveValidationFields({
    transmissionType: formValues.transmissionType,
    orderEmail: formValues.orderEmail,
    previousValidationStatus: 0,
    previousIsValidation: false,
    previousOrderEmail: '',
    isCreate: true,
  });

  return mapValuesToRequest(
    formValues,
    { offlineSupplierId: null, supplierClientId: null },
    validation
  );
};

interface MapEditFormToOfflineUpsertRequestParams {
  values: SupplierFormValues;
  supplier: ManageSupplierEditContext;
  fieldAccess: SupplierFieldAccess;
}

export const mapEditFormToOfflineUpsertRequest = ({
  values,
  supplier,
  fieldAccess,
}: MapEditFormToOfflineUpsertRequestParams): OfflineSupplierUpsertRequest => {
  const resolvedValues = restoreEmailTransmissionIfNeeded(
    supplier,
    resolveOfflineUpsertValues({
      values,
      supplier,
      fieldAccess,
    }),
    fieldAccess
  );

  const validation = resolveValidationFields({
    transmissionType: resolvedValues.transmissionType,
    orderEmail: resolvedValues.orderEmail,
    previousValidationStatus: supplier.validationStatus,
    previousIsValidation: supplier.isValidation,
    previousOrderEmail: supplier.email,
    previousTransmissionType: supplier.transmissionType,
    isCreate: false,
  });

  return mapValuesToRequest(
    resolvedValues,
    {
      offlineSupplierId: supplier.offlineSupplierId ?? null,
      supplierClientId: supplier.supplierClientId ?? null,
    },
    validation
  );
};

export const mapManageSupplierToOnlineUpsertRequest = (
  supplier: Pick<ManageSupplier, 'supplierName' | 'status' | 'poTransmitMethod'>,
  formValues: SupplierFormValues,
  supplierClientId: number
): OfflineSupplierUpsertRequest => ({
  supplierClientId,
  supplierCompanyName: toSupplierFieldValue(supplier.supplierName).trim(),
  externalSupplierName: toUpsertNullableString(formValues.externalSupplierName),
  apId: toUpsertNullableString(formValues.apId),
  status: toApiSupplierStatus(supplier.status),
  poTransmitMethod: supplier.poTransmitMethod,
});
